// RescheduleCampaignModal is used for DropDown
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { Button, DatePicker } from '@components/common'
import './RescheduleCampaignStyle2.css'
import { ICampaignRequest } from '@dataTypes/marketing.type'
import { UPLOAD_TIME_OPTIONS } from '@constants/Marketing'
import { format } from 'date-fns'
import { ChangedEventArgs } from '@syncfusion/ej2-react-calendars'
import { useAppDispatch } from '@store/hooks'
import { updateCampaign } from '@store/actions'
import { getSecondsFromTime } from '@utils/marketing.utils'

type RescheduleCampaignProps = {
  campaign: ICampaignRequest
  show: boolean
  onHide: () => void
}

const RescheduleCampaignModal2: React.FC<RescheduleCampaignProps> = ({
  campaign,
  show,
  onHide,
}) => {
  const dispatch = useAppDispatch()
  const [startDate, setStartDate] = useState<string>(
    campaign.start_date?.toLocaleString() ?? '',
  )
  const [startTime, setStartTime] = useState<string>(campaign.start_time ?? '')
  const [endDate, setEndDate] = useState<string>(
    campaign.end_date?.toLocaleString() ?? '',
  )
  const [endTime, setEndTime] = useState<string>(campaign.end_time ?? '')
  const [validationErrors, setValidationErrors] = useState<
    Partial<Record<keyof ICampaignRequest, string>>
  >({})

  const onStartDateChange = (args: ChangedEventArgs): void => {
    setStartDate(format(args.value as Date, 'MM/dd/yyyy'))
    setValidationErrors({})
  }

  const onEndDateChange = (args: ChangedEventArgs): void => {
    setEndDate(format(args.value as Date, 'MM/dd/yyyy'))
    setValidationErrors({})
  }

  const validateFields = (): { [key: string]: string } => {
    const errors: { [key: string]: string } = {}
    const _startDate = new Date(startDate)
    _startDate.setHours(0, 0, 0, 0)
    const _endDate = new Date(endDate)
    _endDate.setHours(0, 0, 0, 0)

    if (_startDate.getTime() === _endDate.getTime()) {
      if (getSecondsFromTime(startTime) >= getSecondsFromTime(endTime)) {
        errors.end_time = 'End time must be after start time'
      }
    } else {
      if (_startDate > _endDate) {
        errors.end_date = 'End date must be after start date'
      }
    }
    return errors
  }

  const handleUpdate = (): void => {
    const newErrors = validateFields()
    setValidationErrors(newErrors)
    if (Object.keys(newErrors).length === 0) {
      if (campaign.campaign_id) {
        const start_date = format(new Date(startDate), 'MM/dd/yyyy')
        const end_date = format(new Date(endDate), 'MM/dd/yyyy')

        dispatch(
          updateCampaign({
            campaign_id: campaign.campaign_id,
            data: {
              timezone: campaign.timezone,
              start_date,
              end_date,
              start_time: startTime,
              end_time: endTime,
            },
          }),
        ).then(() => {
          onHide()
        })
      }
    }
  }

  const disableUpdateButton = useMemo(() => {
    const startNew = format(new Date(startDate), 'MM/dd/yyyy')
    const startPrevious =
      campaign.start_date && format(campaign.start_date, 'MM/dd/yyyy')

    const endNew = format(new Date(endDate), 'MM/dd/yyyy')
    const endPrevious =
      campaign.end_date && format(campaign.end_date, 'MM/dd/yyyy')

    return (
      startNew === startPrevious &&
      endNew === endPrevious &&
      startTime === campaign.start_time &&
      endTime === campaign.end_time
    )
  }, [startDate, endDate, startTime, endTime])

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="reschedule-campaign-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Reschedule campaign</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="rcm-item">
          <div className="rcm-item-input">
            <span className="rcm-input-label">Start date</span>
            <DatePicker
              id="start-date-picker"
              format="MM/dd/yyyy"
              value={startDate}
              onDateChange={onStartDateChange}
              allowEdit={false}
              showClearButton={false}
            />
          </div>
          <div className="rcm-item-input">
            <span className="rcm-input-label">Start time</span>
            <Form.Select
              name="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            >
              {UPLOAD_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>
        <div className="rcm-item">
          <div className="rcm-item-input">
            <span className="rcm-input-label">End date</span>
            <DatePicker
              id="end-date-picker"
              format="MM/dd/yyyy"
              value={endDate}
              onDateChange={onEndDateChange}
              allowEdit={false}
              showClearButton={false}
            />
            {validationErrors.end_date && (
              <span className="hint-text">{validationErrors.end_date}</span>
            )}
          </div>
          <div className="rcm-item-input">
            <span className="rcm-input-label">End time</span>
            <Form.Select
              name="time"
              value={endTime}
              onChange={(e) => {
                setEndTime(e.target.value)
              }}
              isInvalid={!!validationErrors.end_time}
            >
              {UPLOAD_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {validationErrors.end_time && (
              <span className="hint-text">{validationErrors.end_time}</span>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button className="button-secondary" size="md" onClick={onHide}>
          Cancel
        </Button>
        <Button
          size="md"
          onClick={handleUpdate}
          className="button-primary"
          disabled={disableUpdateButton}
        >
          Update
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default RescheduleCampaignModal2
